import { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Select, Tag, Empty, Spin, Typography } from 'antd';
import { SearchOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { posService } from '@/services/pos.service';
import { formatDate } from '@/utils/format';
import type { PosAvailableSerial } from '../types';

const { Text } = Typography;

interface SerialSwapDialogProps {
  open: boolean;
  onClose: () => void;
  variantId: string;
  productName: string;
  currentSerial?: string;
  usedSerials: string[];
  storeId?: string;
  submitting?: boolean;
  onConfirm: (serial: string) => void;
}

export function SerialSwapDialog({
  open,
  onClose,
  variantId,
  productName,
  currentSerial,
  usedSerials,
  storeId,
  submitting,
  onConfirm,
}: SerialSwapDialogProps) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [warehouseId, setWarehouseId] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<string | null>(currentSerial ?? null);

  useEffect(() => {
    if (open) {
      setSelected(currentSerial ?? null);
      setSearch('');
      setDebounced('');
      setWarehouseId(undefined);
    }
  }, [open, currentSerial]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: serials = [], isFetching } = useQuery<PosAvailableSerial[]>({
    queryKey: ['pos-variant-serials', variantId, storeId, debounced],
    queryFn: () => posService.listVariantSerials(variantId, { q: debounced || undefined }),
    enabled: open && !!variantId,
    staleTime: 10_000,
    retry: false,
  });

  const warehouseOptions = useMemo(() => {
    const map = new Map<string, string>();
    serials.forEach((s) => {
      if (s.warehouse_id && !map.has(s.warehouse_id)) {
        map.set(s.warehouse_id, s.warehouse_name ?? s.warehouse_id);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [serials]);

  const visible = useMemo(
    () => (warehouseId ? serials.filter((s) => s.warehouse_id === warehouseId) : serials),
    [serials, warehouseId],
  );

  const usedSet = useMemo(() => new Set(usedSerials), [usedSerials]);
  const hasChanged = selected !== null && selected !== currentSerial;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Chọn mã quy cách"
      okText="Xác nhận"
      cancelText="Hủy"
      okButtonProps={{ disabled: !hasChanged || submitting, loading: submitting }}
      onOk={() => {
        if (selected) onConfirm(selected);
      }}
      width={520}
    >
      <div style={{ marginBottom: 8 }}>
        <Text strong>{productName}</Text>
        {currentSerial && (
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            Mã hiện tại: <Text code>{currentSerial}</Text>
          </Text>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Tìm mã quy cách..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <Select
          style={{ width: 180 }}
          placeholder="Tất cả kho"
          allowClear
          value={warehouseId}
          onChange={(v) => setWarehouseId(v)}
          options={warehouseOptions}
        />
      </div>

      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
        {isFetching ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : visible.length === 0 ? (
          <Empty description="Không có mã quy cách khả dụng" style={{ padding: 24 }} />
        ) : (
          visible.map((s) => {
            const isUsed = usedSet.has(s.serial);
            const isSelected = s.serial === selected;
            const isCurrent = s.serial === currentSerial;
            return (
              <div
                key={s.serial}
                onClick={() => {
                  if (!isUsed) setSelected(isSelected ? null : s.serial);
                }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  borderBottom: '1px solid #f5f5f5',
                  cursor: isUsed ? 'not-allowed' : 'pointer',
                  background: isSelected ? '#fff0f3' : isCurrent ? '#fafafa' : '#fff',
                  opacity: isUsed ? 0.55 : 1,
                }}
              >
                <div>
                  <div>
                    <Text strong style={{ fontFamily: 'monospace' }}>
                      {s.serial}
                    </Text>
                    {s.weight ? (
                      <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                        ({s.weight}g)
                      </Text>
                    ) : null}
                    {isCurrent && <Tag style={{ marginLeft: 6 }}>Hiện tại</Tag>}
                    {isUsed && <Tag style={{ marginLeft: 6 }}>Đã trong đơn</Tag>}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {s.warehouse_name ?? '—'}
                    {s.received_at ? ` • Nhập ${formatDate(s.received_at)}` : ''}
                  </Text>
                </div>
                {isSelected && <CheckCircleFilled style={{ color: '#a4193d' }} />}
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}
